export type ShortlistFormat = "html" | "markdown";

/**
 * Account tiers. admin sees everything; miton is a Miton colleague (Alister
 * without the admin UI, plus the watchlist and the handoff into Miton Talent,
 * @miton.cz addresses only); user is an external beta customer, Alister only.
 */
export type UserRole = "admin" | "miton" | "user";
export const USER_ROLES: UserRole[] = ["user", "miton", "admin"];

export interface AuthUser {
  id: number;
  email: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  preferred_shortlist_format: ShortlistFormat;
  deep_research_unlimited: boolean;
  totp_enabled: boolean;
  /** A Miton person (miton/admin role on @miton.cz): owns the /miton hub. Decided by the API. */
  miton_access: boolean;
  /** May be handed into Miton Talent from this environment. Decided by the API. */
  talent_access: boolean;
  /** Miton Talent's public origin (for the embedded chat), or null when not configured. */
  talent_url: string | null;
}

interface LoginResponse {
  user: AuthUser | null;
  mfa_required: boolean;
  mfa_token: string | null;
}

/**
 * Either we are signed in, or the account has 2FA and owes a code. The
 * `mfaToken` is a short-lived ticket to hand back to `loginWithTotp`.
 */
export type LoginResult =
  | { kind: "signed-in"; user: AuthUser }
  | { kind: "mfa-required"; mfaToken: string };

interface MeResponse {
  user: AuthUser;
}

interface LogoutResponse {
  ok: true;
}

const AUTH_BASE = "/api/v1/auth";

/**
 * Pull the human-readable message out of an API error body.
 *
 * The API has two shapes: the error envelope from
 * `apps/api/app/middleware/error_handler.py` (`{error: {code, message}}`) for a
 * plain string `HTTPException` detail, and a passthrough `{detail: ...}` when
 * the detail is structured. Read both, else fall back.
 */
async function errorMessage(response: Response, fallback: string): Promise<string> {
  const payload = await response.json().catch(() => null);
  if (payload && typeof payload === "object") {
    const envelope = (payload as { error?: { message?: unknown } }).error;
    if (envelope && typeof envelope.message === "string") return envelope.message;
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await fetch(`${AUTH_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password })
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response, "Invalid credentials"));
  }

  const data: LoginResponse = await response.json();
  if (data.mfa_required && data.mfa_token) {
    return { kind: "mfa-required", mfaToken: data.mfa_token };
  }
  if (!data.user) {
    throw new Error("Login failed");
  }
  return { kind: "signed-in", user: data.user };
}

/** Second leg of a 2FA login: exchange the ticket + authenticator code. */
export async function loginWithTotp(mfaToken: string, code: string): Promise<AuthUser> {
  const response = await fetch(`${AUTH_BASE}/login/2fa`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ mfa_token: mfaToken, code })
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response, "Invalid authentication code"));
  }

  const data: LoginResponse = await response.json();
  if (!data.user) {
    throw new Error("Login failed");
  }
  return data.user;
}

// --- Self-service password: change, forgotten-password reset ---

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to change password"));
  }
}

/**
 * Ask for a reset link. Resolves the same way for unknown addresses — the
 * backend deliberately refuses to confirm whether an account exists.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/password-reset/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to send reset email"));
  }
}

export async function validateResetToken(token: string): Promise<boolean> {
  const response = await fetch(
    `${AUTH_BASE}/password-reset/validate?token=${encodeURIComponent(token)}`
  );
  if (!response.ok) return false;
  const data: { valid: boolean } = await response.json();
  return data.valid;
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
  totpCode?: string
): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/password-reset/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token,
      new_password: newPassword,
      totp_code: totpCode || null,
    }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to reset password"));
  }
}

// --- Two-factor authentication (TOTP) ---

export interface TotpSetup {
  secret: string;
  otpauth_uri: string;
  qr_data_uri: string;
}

export async function setupTotp(): Promise<TotpSetup> {
  const response = await fetch(`${AUTH_BASE}/2fa/setup`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to start 2FA setup"));
  }
  return response.json();
}

export async function enableTotp(code: string): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/2fa/enable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Invalid authentication code"));
  }
}

export async function disableTotp(password: string, code: string): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/2fa/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ password, code }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to disable 2FA"));
  }
}

/** Admin lockout recovery — clears a user's 2FA when they lose the authenticator. */
export async function adminDisableTotp(userId: number): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/users/${userId}/disable-2fa`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to disable 2FA"));
  }
}

export async function logout(): Promise<void> {
  await fetch(`${AUTH_BASE}/logout`, {
    method: "POST",
    credentials: "include"
  });
}

// --- User management (admin) ---

interface UsersListResponse {
  users: AuthUser[];
}

export async function getUsers(): Promise<AuthUser[]> {
  const response = await fetch(`${AUTH_BASE}/users`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.status}`);
  }

  const data: UsersListResponse = await response.json();
  return data.users;
}

export async function createUser(
  email: string,
  password: string,
  role: UserRole = "user"
): Promise<AuthUser> {
  const response = await fetch(`${AUTH_BASE}/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password, role }),
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to create user"));
  }

  return response.json();
}

export async function updateUser(
  id: number,
  updates: {
    role?: UserRole;
    is_active?: boolean;
    preferred_shortlist_format?: ShortlistFormat;
    deep_research_unlimited?: boolean;
  }
): Promise<AuthUser> {
  const response = await fetch(`${AUTH_BASE}/users/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update user: ${response.status}`);
  }

  return response.json();
}

export async function deleteUser(id: number): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/users/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to delete user"));
  }
}

export async function resetPassword(id: number, newPassword: string): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/users/${id}/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ new_password: newPassword }),
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to reset password"));
  }
}

// --- MCP API keys (admin) ---

export interface McpKey {
  id: number;
  user_id: number;
  user_email: string;
  name: string;
  key_prefix: string;
  plaintext: string | null;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

interface McpKeysListResponse {
  keys: McpKey[];
}

interface CreateMcpKeyResponse {
  key: McpKey;
  plaintext: string;
}

export async function getMcpKeys(userId?: number): Promise<McpKey[]> {
  const qs = userId !== undefined ? `?user_id=${userId}` : "";
  const response = await fetch(`${AUTH_BASE}/mcp-keys${qs}`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch MCP keys: ${response.status}`);
  }
  const data: McpKeysListResponse = await response.json();
  return data.keys;
}

export interface DuplicateActiveKeyError {
  kind: "duplicate_active_key";
  existingId: number;
  existingPrefix: string;
  message: string;
}

export class McpKeyConflictError extends Error {
  existingId: number;
  existingPrefix: string;
  constructor(detail: { existing_id: number; existing_prefix: string; message: string }) {
    super(detail.message);
    this.name = "McpKeyConflictError";
    this.existingId = detail.existing_id;
    this.existingPrefix = detail.existing_prefix;
  }
}

export async function createMcpKey(
  userId: number,
  name: string,
  replace: boolean = false
): Promise<CreateMcpKeyResponse> {
  const response = await fetch(`${AUTH_BASE}/mcp-keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ user_id: userId, name, replace }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    if (response.status === 409 && payload && typeof payload === "object") {
      const detail = (payload as { detail?: unknown }).detail;
      if (
        detail &&
        typeof detail === "object" &&
        "existing_id" in detail &&
        "existing_prefix" in detail
      ) {
        throw new McpKeyConflictError(
          detail as { existing_id: number; existing_prefix: string; message: string }
        );
      }
    }
    const message =
      payload && typeof payload === "object" && "detail" in payload
        ? String((payload as { detail: string }).detail)
        : "Failed to create key";
    throw new Error(message);
  }
  return response.json();
}

export async function getMyMcpKey(): Promise<McpKey | null> {
  const response = await fetch(`${AUTH_BASE}/my-mcp-key`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch MCP key: ${response.status}`);
  }
  const data: { key: McpKey | null } = await response.json();
  return data.key;
}

export async function revokeMcpKey(id: number): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/mcp-keys/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to revoke key: ${response.status}`);
  }
}


// --- WorkOS AuthKit Connect (Standalone) completion ---

export class WorkOSCompletionError extends Error {
  workosStatus: number | undefined;
  constructor(message: string, workosStatus?: number) {
    super(message);
    this.name = "WorkOSCompletionError";
    this.workosStatus = workosStatus;
  }
}

/**
 * Tell our backend to resume an AuthKit Standalone flow after the user
 * has signed in. Returns the URL the browser should be redirected to
 * next (back into AuthKit so it can mint the OAuth token for the MCP
 * client that initiated the flow).
 */
export async function completeWorkOSAuth(externalAuthId: string): Promise<string> {
  const response = await fetch(`${AUTH_BASE}/workos/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ external_auth_id: externalAuthId }),
  });

  if (!response.ok) {
    let message = "AuthKit completion failed";
    let workosStatus: number | undefined;
    try {
      const body = await response.json();
      if (body && typeof body === "object" && "detail" in body) {
        const detail = (body as { detail: unknown }).detail;
        if (typeof detail === "string") {
          message = detail;
        } else if (detail && typeof detail === "object") {
          const d = detail as { message?: string; workos_status?: number };
          if (d.message) message = d.message;
          if (d.workos_status) workosStatus = d.workos_status;
        }
      }
    } catch {
      // leave default message
    }
    throw new WorkOSCompletionError(message, workosStatus);
  }

  const data: { redirect_uri: string } = await response.json();
  return data.redirect_uri;
}


export async function getMe(): Promise<AuthUser | null> {
  try {
    const response = await fetch(`${AUTH_BASE}/me`, {
      method: "GET",
      credentials: "include"
    });

    if (!response.ok) {
      return null;
    }

    const data: MeResponse = await response.json();
    return data.user;
  } catch {
    return null;
  }
}
